/**
 * Input hygiene for public lead-capture forms. Framework-free (no React/Node) so
 * the web server, the client bundle, and the form blocks all share ONE
 * implementation. Three guards:
 *   - looksMalicious : reject XSS / script-injection payloads outright.
 *   - containsSlang  : reject profanity/abuse (spam barrier).
 *   - sanitizeText   : defence-in-depth — strip tags/control chars before store.
 * The server enforces all three (authoritative); clients call them for instant
 * feedback. Lead text is also escaped at every output (admin renders React text
 * nodes; the email escapes values) so stored-XSS is neutralised regardless.
 *
 * The profanity check is ported from the cymmetri-lotus profile-bio moderation
 * (packages/shared/src/text-moderation.ts) so both products share one policy —
 * obfuscation tolerance (sh1t / f.u.c.k / fuuuck), boundary policies to avoid the
 * Scunthorpe problem, a safe-word list, English + Hindi/Marathi terms, and a
 * name-field allowance for words that are also real names (Dick, Lund, Randi).
 */

// ── XSS / script-injection detection. Any match → reject the submission. ──────
const MALICIOUS_RES: RegExp[] = [
  /<\s*script/i, // <script ...  (even without a closing >)
  /<[a-z!/][^>]*>/i, // any closed HTML/comment tag: <b>, <img ...>, </x>, <!--...-->
  /javascript\s*:/i,
  /vbscript\s*:/i,
  /data\s*:\s*text\/html/i,
  /data\s*:[^,]*;\s*base64/i,
  /\son\w+\s*=/i, // event-handler attribute: onerror=, onload=, onclick=
  /document\s*\.\s*(?:cookie|write|location|domain)/i,
  /window\s*\.\s*(?:location|open)\b/i,
  /\.\s*innerHTML/i,
  /\beval\s*\(/i,
  /\bimport\s*\(/i,
  /\bnew\s+Function\s*\(/i,
];

/** True if any supplied text looks like an XSS / JS-injection attempt. */
export function looksMalicious(...texts: Array<string | null | undefined>): boolean {
  return texts.some((t) => !!t && MALICIOUS_RES.some((re) => re.test(t)));
}

/** Strip tag-like sequences + control chars, collapse whitespace, cap length. */
export function sanitizeText(input: string | null | undefined, maxLen = 2000): string {
  if (!input) return '';
  let s = String(input)
    .replace(/<[^>]*>/g, ' ') // drop anything tag-shaped
    .replace(/[\u0000-\u001F\u007F]/g, " ") // control chars
    .replace(/\s+/g, ' ')
    .trim();
  if (s.length > maxLen) s = s.slice(0, maxLen);
  return s;
}

// ── Profanity / slang (ported from cymmetri-lotus text-moderation) ────────────

type BoundaryPolicy = 'word' | 'prefix';
interface BlockedTerm {
  term: string;
  boundary: BoundaryPolicy;
}

/** Words legitimate in somebody's name — allowed in name fields only. */
const ALLOWED_IN_NAMES: ReadonlySet<string> = new Set(['dick', 'lund', 'randi']);

/** Ordinary words removed before matching, so repeated-letter tolerance doesn't
 *  turn "shiitake"/"cocktail"/"documentation" into false hits. */
const SAFE_WORDS: readonly string[] = [
  'shiitake', 'scunthorpe', 'assassin', 'assassinate', 'assassination', 'cocktail', 'cockpit',
  'peacock', 'hancock', 'dickens', 'dickinson', 'cummins', 'cumbria', 'cumulative', 'accumulate',
  'circumstance', 'document', 'documentation',
];
const SAFE_WORD_PATTERN = new RegExp(`(?<![a-z])(?:${SAFE_WORDS.join('|')})(?![a-z])`, 'g');

const BLOCKED_TERMS: readonly BlockedTerm[] = [
  // English, unambiguous enough to catch as a prefix.
  { term: 'fuck', boundary: 'prefix' }, { term: 'shit', boundary: 'prefix' },
  { term: 'bitch', boundary: 'prefix' }, { term: 'bastard', boundary: 'prefix' },
  { term: 'asshole', boundary: 'prefix' }, { term: 'arsehole', boundary: 'prefix' },
  { term: 'motherfuck', boundary: 'prefix' }, { term: 'dickhead', boundary: 'prefix' },
  { term: 'dumbass', boundary: 'prefix' }, { term: 'jackass', boundary: 'prefix' },
  { term: 'whore', boundary: 'prefix' }, { term: 'slut', boundary: 'prefix' },
  { term: 'cunt', boundary: 'prefix' }, { term: 'wank', boundary: 'prefix' },
  { term: 'bollock', boundary: 'prefix' }, { term: 'twat', boundary: 'prefix' },
  { term: 'cocksuck', boundary: 'prefix' }, { term: 'douchebag', boundary: 'prefix' },
  { term: 'nigger', boundary: 'prefix' }, { term: 'nigga', boundary: 'prefix' },
  { term: 'faggot', boundary: 'prefix' }, { term: 'retard', boundary: 'prefix' },
  { term: 'crap', boundary: 'prefix' },
  { term: 'phuck', boundary: 'prefix' }, { term: 'fuk', boundary: 'word' },
  { term: 'fck', boundary: 'word' }, { term: 'bullshit', boundary: 'prefix' },
  // English, needs to stand alone — each sits inside ordinary words.
  { term: 'ass', boundary: 'word' }, { term: 'arse', boundary: 'word' },
  { term: 'dick', boundary: 'word' }, { term: 'prick', boundary: 'word' },
  { term: 'cum', boundary: 'word' }, { term: 'tits', boundary: 'word' },
  { term: 'pussy', boundary: 'word' }, { term: 'piss', boundary: 'word' },
  { term: 'fag', boundary: 'word' }, { term: 'hoe', boundary: 'word' },
  // Hindi / Marathi, transliterated the way people actually type it.
  { term: 'chutiya', boundary: 'prefix' }, { term: 'chutiye', boundary: 'prefix' },
  { term: 'chutia', boundary: 'prefix' }, { term: 'bhenchod', boundary: 'prefix' },
  { term: 'behenchod', boundary: 'prefix' }, { term: 'bhenchd', boundary: 'prefix' },
  { term: 'madarchod', boundary: 'prefix' }, { term: 'madarchood', boundary: 'prefix' },
  { term: 'bhosdi', boundary: 'prefix' }, { term: 'bhosda', boundary: 'prefix' },
  { term: 'bhosad', boundary: 'prefix' }, { term: 'gandu', boundary: 'prefix' },
  { term: 'gaandu', boundary: 'prefix' }, { term: 'gaand', boundary: 'prefix' },
  { term: 'harami', boundary: 'prefix' }, { term: 'haramkhor', boundary: 'prefix' },
  { term: 'kamina', boundary: 'prefix' }, { term: 'kamine', boundary: 'prefix' },
  { term: 'chinal', boundary: 'prefix' }, { term: 'jhatu', boundary: 'prefix' },
  { term: 'chodu', boundary: 'prefix' }, { term: 'lauda', boundary: 'word' },
  { term: 'laude', boundary: 'word' }, { term: 'lund', boundary: 'word' },
  { term: 'randi', boundary: 'word' }, { term: 'kutiya', boundary: 'word' },
  { term: 'kutti', boundary: 'word' },
];

/** Characters people substitute for letters — resolved before matching. */
const SUBSTITUTIONS: Record<string, string> = {
  '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't', '8': 'b',
  '@': 'a', $: 's', '!': 'i', '+': 't',
};

/** Lower-case, fold accents, resolve digit/symbol substitutions (length-preserving). */
function normalise(input: string): string {
  return input
    .normalize('NFKC')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, "") // combining marks only
    .replace(/[0134578@$!+]/g, (char) => SUBSTITUTIONS[char] ?? char);
}

/** Build the pattern for one term: every letter may repeat and be separated by
 *  non-alphanumerics; lookaround enforces the boundary policy. */
function buildPattern({ term, boundary }: BlockedTerm): RegExp {
  const body = term.split('').map((char) => `${char}+`).join('[^a-z0-9]*');
  const tail = boundary === 'word' ? '(?![a-z])' : '';
  return new RegExp(`(?<![a-z])${body}${tail}`);
}

const PATTERNS: readonly (BlockedTerm & { pattern: RegExp })[] = BLOCKED_TERMS.map((entry) => ({
  ...entry,
  pattern: buildPattern(entry),
}));

export interface ModerationOptions {
  /** Treat the text as a name, allowing the few terms that are also real names. */
  isName?: boolean;
}

/** The blocked terms found in a piece of text (canonical form, not what was typed). */
export function findBlockedTerms(input: string | null | undefined, options: ModerationOptions = {}): string[] {
  if (!input) return [];
  const haystack = normalise(input).replace(SAFE_WORD_PATTERN, ' ');
  const found: string[] = [];
  for (const { term, pattern } of PATTERNS) {
    if (options.isName && ALLOWED_IN_NAMES.has(term)) continue;
    if (pattern.test(haystack)) found.push(term);
  }
  return found;
}

export function containsBlockedTerms(input: string | null | undefined, options: ModerationOptions = {}): boolean {
  return findBlockedTerms(input, options).length > 0;
}

/** True if any supplied text contains blocked profanity/slang. */
export function containsSlang(...texts: Array<string | null | undefined>): boolean {
  return texts.some((t) => containsBlockedTerms(t));
}

export interface HygieneResult {
  ok: boolean;
  reason?: 'malicious' | 'slang';
}

/**
 * One-shot check over a submission. `textValues` are ordinary fields; `nameValues`
 * are name fields (checked with the name allowance so "Dick"/"Randi" aren't refused).
 */
export function checkFormHygiene(
  textValues: Array<string | null | undefined>,
  nameValues: Array<string | null | undefined> = [],
): HygieneResult {
  if (looksMalicious(...textValues, ...nameValues)) return { ok: false, reason: 'malicious' };
  if (textValues.some((t) => containsBlockedTerms(t))) return { ok: false, reason: 'slang' };
  if (nameValues.some((t) => containsBlockedTerms(t, { isName: true }))) return { ok: false, reason: 'slang' };
  return { ok: true };
}
